import React, { FC, useEffect, useState } from 'react'
import '@components/atoms/textfield/textfield.css'
import './one-time-password.css'

// UTILITIES
import { flatten } from '@utils/string-helper'
import { UseInputHookProps } from '@hooks/inputs'


interface Props extends UseInputHookProps {
    className?: string
    style?: React.CSSProperties
    /** Number of characters in the one time password. Defaults to 6. */
    otpLength?: number
}

const idBase = "otp-"

/** Acts like a standard textField. Compatible with the useInput hook. Reference this guide for OTP form best practices: https://www.twilio.com/blog/html-attributes-two-factor-authentication-autocomplete
 * @param otpLength Number of characters in the one time password. Defaults to 6.
 * @param onChange Standard Input Form Event.
 * @param value Mutable value stored in state.
 */
const OneTimePassword: FC<Props> = (props) => {
    const { className: classProps, otpLength = 6, onChange, value: valueProp, style } = props
    const [values, setValues] = useState<string[]>([...Array(otpLength)].map((_, i) => (valueProp?.[i] ?? '')))
    const className = flatten("one-time-password", classProps)

    /** Keeps the seperated values Array in sync with the value param. Primarily to handle the setValue function method provided by the useInput hook. */
    useEffect(() => {
        setValues([...Array(otpLength)].map((_, i) => (valueProp?.[i] ?? '')))
    }, [valueProp])

    /** Calls the useInput hook onChange event. Allows this Component to function as a standard input for the purposes of consistency. */
    useEffect(() => {
        if (onChange) onChange({ currentTarget: { value: values.join('') } } as any)
    }, [onChange, values])

    /** Checks to see if the SMS_OTP API is available in the browser. Currently only supported on `chrome`, this api allows the browser to parse and submit the
     *  one time password from the SMS message sent. Reference this guide for more information: https://web.dev/sms-otp-form/  */
    useEffect(() => {
        if ('OTPCredential' in window) {
            async function event() {
                const input = document.querySelector('input[autocomplete="one-time-code"]') as (HTMLInputElement | null)
                if (!input) return
                const ac = new AbortController()
                const form = input.closest('form')
                if (form) form.addEventListener('submit', () => { ac.abort() })
                try {
                    const otp = await navigator.credentials.get({ otp: { transport: ['sms'] }, signal: ac.signal })
                    if (otp?.code != null && otp.code !== '') onChange({ currentTarget: { value: otp.code } } as any)
                } catch (error) {
                    console.log(error)
                }
            }
            window.addEventListener('DOMContentLoaded', event)
            return () => { window.removeEventListener('DOMContentLoaded', event) }
        }
    }, [])

    /** Handles the `backspace` event. This is primarily used for mobile devices since calling focus on an input on such a device can get a little weird.
     *  This adds more consistency when the, relative to device, backspace button is pressed. */
    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        const { key, currentTarget: { id } } = e
        const index = Number(id.replace(idBase, ''))
        var arr = [...values]
        if (!Number.isNaN(index) && arr?.[index] != null && key === "Backspace") {
            document.getElementById(`${idBase}${(index !== 0 ? index - 1 : index)}`)?.focus()
            arr[index] = ''
            setValues(arr)
            e.preventDefault()
        }
    }

    /** The primary handler for storing the given values to state. */
    function handleChange(e: React.FormEvent<HTMLInputElement>) {
        if (/^\d+$/.test(e.currentTarget.value) || e.currentTarget.value === '') {
            const { id, value } = e.currentTarget
            const index = Number(id.replace(idBase, ''))
            var arr = [...values]
            if (!Number.isNaN(index) && arr?.[index] != null) {
                document.getElementById(`${idBase}${(value !== '' ? index + 1 : index - 1)}`)?.focus()
                arr[index] = value
                setValues(arr)
            }
        }
    }

    return (
        <div className={className} style={style}>
            <div className="inputs">
                {[...Array(otpLength)].map((_, i) => (
                    <input key={`${idBase}${i}`}
                        id={`${idBase}${i}`}
                        className="textfield"
                        inputMode="numeric"
                        autoComplete="off"
                        autoCorrect="off"
                        type="text"
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        value={values?.[i] ?? ''}
                    />
                ))}
            </div>
        </div>
    )
}

export default OneTimePassword